import { type ChangeEvent, type HTMLProps, type ReactNode, forwardRef, useCallback, useId, useImperativeHandle, useRef } from 'react';
import { type Theme, css } from '@emotion/react';
import { WarningCircle } from '@phosphor-icons/react';
import { useEditAppValidation } from 'client/lib/EditApp';
import { Typography } from 'client/components/Typography';
import { Easing, Font, Transition, applyEffect, borderRadius } from 'client/Theme';
import { InputIcon } from './InputIcon';
import { useInputCopy } from './inputUtils';

export interface InputProps extends Omit<HTMLProps<HTMLInputElement>, 'label' | 'value' | 'onChange'> {
  value?: string;
  label: ReactNode;
  large?: boolean;
  multiline?: boolean;
  disableAutocomplete?: boolean;
  hideEditIcon?: boolean;
  endAdornment?: ReactNode | null;
  openLinkInNewTab?: string | null;
  canCopy?: boolean;
  path?: string;
  error?: JSX.Element | string | null | false;
  icon?: JSX.Element;
  maxRows?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: VoidFunction;
  onReset?: VoidFunction;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id: idProp,
      label,
      value = '',
      placeholder,
      endAdornment = null,
      large = false,
      multiline = false,
      canCopy = false,
      openLinkInNewTab = null,
      path,
      error: errorProp = false,
      icon,
      maxRows = 2,
      className,
      disableAutocomplete,
      hideEditIcon = false,
      onEnter,
      ...other
    },
    ref
  ) => {
    const autoId = useId();
    const id = idProp || autoId;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { isCopyTooltipOpen, handleCopy } = useInputCopy({ canCopy, inputRef });
    const validationError = useEditAppValidation(path || null);

    const error = validationError || errorProp;

    const isEditHidden = hideEditIcon || (value && value.length > 0) || !!placeholder;
    const isAction = canCopy || !!openLinkInNewTab;

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleKeyPress = useCallback(
      (event: React.KeyboardEvent) => {
        if (onEnter && event.key === 'Enter') onEnter();
      },
      [onEnter]
    );

    let container = <div />;
    if (openLinkInNewTab) container = <a href={openLinkInNewTab} target="_blank" />;

    let input = <input />;
    if (multiline) input = <textarea rows={maxRows} />;

    return (
      <container.type {...container.props} className={`input ${className || ''}`} css={containerStyle}>
        <input.type
          id={id}
          value={value}
          placeholder={placeholder}
          onKeyPress={handleKeyPress}
          {...input.props}
          {...other}
          disableautocomplete={`${disableAutocomplete || false}`}
          readOnly={isAction}
          ref={inputRef}
          onClick={other.onClick || handleCopy}
          css={[inputStyle, large && largeInputStyle, error && errorInputStyle]}
        />
        <Typography component="label" htmlFor={id} css={labelStyle}>
          {label}
          {!!error && (
            <span>
              {' '}
              — <WarningCircle weight="bold" aria-hidden /> {error}
            </span>
          )}
        </Typography>
        <InputIcon isCopyTooltipOpen={isCopyTooltipOpen} icon={icon} canCopy={canCopy} openLinkInNewTab={openLinkInNewTab} />
        <span css={[decoratorStyle, !isEditHidden && editDecoratorStyle]} />
        {endAdornment}
      </container.type>
    );
  }
);

const createEditIconURI = (color: string) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `fill='${color.replace('#', '%23')}' viewBox='0 0 256 256'%3E%3Crect width='256' height='256' ` +
  `fill='none'%3E%3C/rect%3E%3Cpath d='M92.7,216H48a8,8,0,0,1-8-8V163.3a7.9,7.9,0,0,1,2.3-5.6l120-120a8,8,0,0,1,11.4,` +
  `0l44.6,44.6a8,8,0,0,1,0,11.4l-120,120A7.9,7.9,0,0,1,92.7,216Z' fill='none' ` +
  `stroke='${color.replace('#', '%23')}' stroke-linecap='round' stroke-linejoin='round' stroke-width='24'%3E%3C/path%3E%3Cline ` +
  `x1='136' y1='64' x2='192' y2='120' fill='none' stroke='${color.replace('#', '%23')}' stroke-linecap='round' stroke-linejoin='round' ` +
  `stroke-width='24'%3E%3C/line%3E%3C/svg%3E")`;

const editIconURI = (theme: Theme) => createEditIconURI(theme.color.foreground);
const editIconErrorURI = (theme: Theme) => createEditIconURI(theme.color.red[100]);

const errorIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' viewBox='0 0 256 256'%3E%3Cpath ` +
  `fill='none' d='M0 0h256v256H0z'/%3E%3Cpath fill='${theme.color.red[100].replace('#', '%23')}' d='M128 24a104 104 0 1 0 104 ` +
  `104A104 104 0 0 0 128  24Zm-8 56a8 8 0 0 1 16 0v56a8 8 0 0 1-16 0Zm8 104a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z'/%3E%3C/svg%3E")`;

const containerStyle = css`
  position: relative;
  display: flex;
  width: 100%;
  max-width: min(100%, 65ch);
  text-decoration: none;
`;

const inputStyle = (theme: Theme) => css`
  flex: 1;
  padding: 25px 12px 8px 12px;
  min-width: 0;
  line-height: 23px;
  font-size: 1em;
  font-family: ${Font.body};
  text-align: left;
  text-decoration: none;
  background-color: ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;
  border: none;
  outline: none;
  color: ${theme.color.foreground};
  resize: none;

  box-sizing: border-box;

  transition: background-color ${Transition.basic};

  &:hover {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};

    & ~ .icon {
      color: ${theme.color.foreground};
    }

    & ~ .icon > p {
      color: ${theme.color.foreground};
    }
  }

  &:focus:not([readonly]) {
    background-color: ${theme.color.primary[60]};

    & ~ label {
      color: ${theme.color.primary[100]};
    }

    & ~ span::before {
      content: none;
    }
  }

  &:disabled {
    background-color: ${theme.color.grey[60]};
    opacity: 0.5;
    cursor: not-allowed;
    -webkit-text-fill-color: ${theme.color.grey[100]};
    color: ${theme.color.grey[100]};

    & ~ label {
      opacity: 0.5;
    }

    & ~ span::before {
      opacity: 0.1;
    }
  }

  &[readonly] {
    cursor: pointer;
  }
`;

const largeInputStyle = css`
  font-size: 1.85rem;

  & ~ span::before {
    bottom: 14px;
    width: 1.5em;
    height: 1.5em;
    opacity: 0.15;
  }
`;

const errorInputStyle = (theme: Theme) => css`
  background-color: ${theme.color.red[60]};

  &:hover {
    background-color: ${applyEffect(theme.color.red[60], 'hover')};
  }

  & ~ label {
    color: ${theme.color.red[100]};
  }

  & ~ span::before {
    background-image: ${editIconErrorURI(theme)};
    opacity: 0.3;
  }

  & ~ span::after {
    transform: translate(0px, -50%) scale(1);
    opacity: 1;
  }
`;

const labelStyle = (theme: Theme) => css`
  position: absolute;
  align-self: flex-start;
  font-weight: 700 !important;
  font-size: 16px;
  transform-origin: 0 50%;
  transform: translate(12px, 3px) scale(0.75);
  user-select: none;
  pointer-events: none;

  transition: color ${Transition.basic};

  > span {
    color: ${theme.color.red[100]};
    opacity: 0.8;

    svg {
      transform: translateY(3px);
    }
  }
`;

const decoratorStyle = (theme: Theme) => css`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    right: -48px;
    width: 24px;
    height: 24px;
    background-image: ${errorIconURI(theme)};
    background-size: 100%;
    transform: translate(-48px, -50%) scale(0.5);
    opacity: 0;

    transition: transform ${Easing.springy} 300ms, opacity ${Easing.springy} 200ms;
  }
`;

const editDecoratorStyle = (theme: Theme) => css`
  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 10px;
    bottom: 10px;
    width: 1em;
    height: 1em;
    background-image: ${editIconURI(theme)};
    background-size: 100%;
    opacity: 0.2;
  }
`;
